import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { StyleSheet } from "react-native";
import { style, viewportBase, webStyle } from "./Carousel.style";
const DRAG_START_THRESHOLD = 6;
export const FADE_DURATION = 300;
const SCROLL_END_DELAY = 150;
const SNAP_DISTANCE_THRESHOLD = 0.18;
const SNAP_VELOCITY_THRESHOLD = 0.35;
const SNAP_DISTANCE_THRESHOLD_TOUCH = 0.1;
const SNAP_VELOCITY_THRESHOLD_TOUCH = 0.2;
const PAN_SAMPLES_CAPACITY = 5;
const SPRING_DURATION = 280;
const clamp = (min, max, value) => Math.min(max, Math.max(min, value));
const resolveThresholds = (pointerType) => pointerType === "touch"
    ? { distance: SNAP_DISTANCE_THRESHOLD_TOUCH, velocity: SNAP_VELOCITY_THRESHOLD_TOUCH }
    : { distance: SNAP_DISTANCE_THRESHOLD, velocity: SNAP_VELOCITY_THRESHOLD };
const NOOP = () => void 0;
const Carousel = ({ activeIndex = 0, animationType = "slide", data, enabled = true, children, bullets, onActiveIndexChanged, onSwipeStart = NOOP, onSwipeEnd = NOOP, style: customStyle, }) => {
    const layoutRef = useRef(null);
    const activeIndexRef = useRef(activeIndex);
    activeIndexRef.current = activeIndex;
    const [carouselWidth, setCarouselWidth] = useState(undefined);
    const [isDragging, setIsDragging] = useState(false);
    const [translateX, setTranslateX] = useState(0);
    const styleWeb = useMemo(() => webStyle(), []);
    const pointerActiveRef = useRef(false);
    const dragStartedRef = useRef(false);
    const suppressClickRef = useRef(false);
    const panStartXRef = useRef(0);
    const panStartTranslateXRef = useRef(0);
    const panLastXRef = useRef(0);
    const panLastTimeRef = useRef(0);
    const panVelocityRef = useRef(0);
    const panSamplesRef = useRef([]);
    const pointerTypeRef = useRef("mouse");
    const swipeEndTimeoutRef = useRef(null);
    useEffect(() => {
        if (!layoutRef.current)
            return;
        const observer = new ResizeObserver(([entry]) => {
            setCarouselWidth(entry.contentRect.width);
        });
        observer.observe(layoutRef.current);
        return () => observer.disconnect();
    }, []);
    useEffect(() => {
        return () => {
            if (swipeEndTimeoutRef.current)
                clearTimeout(swipeEndTimeoutRef.current);
        };
    }, []);
    const updateActiveIndex = useCallback((index) => {
        if (activeIndexRef.current === index)
            return;
        onActiveIndexChanged(index);
    }, [onActiveIndexChanged]);
    const clearSwipeEndTimeout = useCallback(() => {
        if (swipeEndTimeoutRef.current)
            clearTimeout(swipeEndTimeoutRef.current);
        swipeEndTimeoutRef.current = null;
    }, []);
    const scheduleSwipeEnd = useCallback(() => {
        clearSwipeEndTimeout();
        swipeEndTimeoutRef.current = setTimeout(() => {
            if (pointerActiveRef.current)
                return;
            onSwipeEnd();
        }, SCROLL_END_DELAY);
    }, [clearSwipeEndTimeout, onSwipeEnd]);
    const handleOnActiveIndexChanged = useCallback((index) => {
        updateActiveIndex(index);
        if (animationType === "slide" && carouselWidth) {
            setTranslateX(-index * carouselWidth);
        }
    }, [animationType, carouselWidth, updateActiveIndex]);
    useEffect(() => {
        if (animationType !== "slide" || !carouselWidth || pointerActiveRef.current)
            return;
        setTranslateX(-activeIndex * carouselWidth);
    }, [activeIndex, animationType, carouselWidth]);
    const finishPointerInteraction = useCallback((target, pointerId) => {
        pointerActiveRef.current = false;
        if (!dragStartedRef.current) {
            suppressClickRef.current = false;
            return;
        }
        dragStartedRef.current = false;
        setIsDragging(false);
        if (target.hasPointerCapture(pointerId))
            target.releasePointerCapture(pointerId);
        scheduleSwipeEnd();
        setTimeout(() => {
            suppressClickRef.current = false;
        }, 0);
    }, [scheduleSwipeEnd]);
    const handlePointerDown = useCallback((e) => {
        if (!enabled || animationType !== "slide" || !carouselWidth)
            return;
        clearSwipeEndTimeout();
        pointerActiveRef.current = true;
        dragStartedRef.current = false;
        panStartXRef.current = e.clientX;
        panStartTranslateXRef.current = translateX;
        panLastXRef.current = e.clientX;
        panLastTimeRef.current = performance.now();
        panVelocityRef.current = 0;
        panSamplesRef.current = [{ x: e.clientX, t: performance.now() }];
        pointerTypeRef.current = e.pointerType;
        suppressClickRef.current = false;
    }, [animationType, carouselWidth, clearSwipeEndTimeout, enabled, translateX]);
    const handlePointerMove = useCallback((e) => {
        if (!pointerActiveRef.current || animationType !== "slide" || !carouselWidth)
            return;
        const dx = e.clientX - panStartXRef.current;
        if (!dragStartedRef.current && Math.abs(dx) < DRAG_START_THRESHOLD)
            return;
        if (!dragStartedRef.current) {
            dragStartedRef.current = true;
            suppressClickRef.current = true;
            setIsDragging(true);
            e.currentTarget.setPointerCapture(e.pointerId);
            onSwipeStart();
        }
        const now = performance.now();
        const sample = { x: e.clientX, t: now };
        panSamplesRef.current.push(sample);
        if (panSamplesRef.current.length > PAN_SAMPLES_CAPACITY)
            panSamplesRef.current.shift();
        const samples = panSamplesRef.current;
        if (samples.length >= 2) {
            const span = samples[samples.length - 1].t - samples[0].t;
            if (span > 0) {
                panVelocityRef.current = (samples[samples.length - 1].x - samples[0].x) / span;
            }
            // if span === 0, keep previous panVelocityRef.current
        }
        panLastXRef.current = e.clientX;
        panLastTimeRef.current = now;
        e.preventDefault();
        const minTranslateX = -(data.length - 1) * carouselWidth;
        const rawTranslateX = panStartTranslateXRef.current + dx;
        setTranslateX(clamp(minTranslateX, 0, rawTranslateX));
    }, [animationType, carouselWidth, data.length, onSwipeStart]);
    const commitSnap = useCallback((dx) => {
        if (!carouselWidth)
            return;
        const { distance: distThreshold, velocity: velThreshold } = resolveThresholds(pointerTypeRef.current);
        const velocityDirection = panVelocityRef.current < 0 ? 1 : -1;
        const velocityStep = Math.abs(panVelocityRef.current) > velThreshold ? velocityDirection : 0;
        const distanceStep = Math.abs(dx) > carouselWidth * distThreshold ? (dx < 0 ? 1 : -1) : 0;
        const nextIndex = clamp(0, data.length - 1, activeIndexRef.current + (velocityStep || distanceStep));
        setTranslateX(-nextIndex * carouselWidth);
        updateActiveIndex(nextIndex);
    }, [carouselWidth, data.length, updateActiveIndex]);
    const handlePointerUp = useCallback((e) => {
        if (!pointerActiveRef.current || animationType !== "slide")
            return;
        if (dragStartedRef.current)
            commitSnap(e.clientX - panStartXRef.current);
        finishPointerInteraction(e.currentTarget, e.pointerId);
    }, [animationType, commitSnap, finishPointerInteraction]);
    const handlePointerCancel = useCallback((e) => {
        if (!pointerActiveRef.current || animationType !== "slide")
            return;
        if (dragStartedRef.current)
            commitSnap(panLastXRef.current - panStartXRef.current);
        finishPointerInteraction(e.currentTarget, e.pointerId);
    }, [animationType, commitSnap, finishPointerInteraction]);
    const handleClickCapture = useCallback((e) => {
        if (!suppressClickRef.current)
            return;
        e.preventDefault();
        e.stopPropagation();
    }, []);
    const trackStyle = useMemo(() => ({
        ...style.carouselTrack,
        display: "flex",
        transform: `translateX(${translateX}px)`,
        transition: isDragging || !carouselWidth ? "none" : `transform ${SPRING_DURATION}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
        width: carouselWidth ? carouselWidth * data.length : "100%",
        willChange: "transform",
    }), [carouselWidth, data.length, isDragging, translateX]);
    const slideViewportStyle = useMemo(() => ({
        ...style.carousel,
        ...viewportBase,
        cursor: enabled ? (isDragging ? "grabbing" : "grab") : "default",
        touchAction: enabled ? "pan-y" : "auto",
        width: "100%",
    }), [enabled, isDragging]);
    const fadeViewportStyle = useMemo(() => ({
        ...style.carousel,
        ...viewportBase,
        display: "grid",
        position: "relative",
        width: "100%",
    }), []);
    return (React.createElement("div", { "data-testid": "carousel", style: {
            ...style.container,
            ...(customStyle?.container ? StyleSheet.flatten(customStyle.container) : undefined),
            overflow: "hidden",
            position: "relative",
            width: "100%",
        } },
        animationType === "fade" ? (React.createElement("div", { ref: layoutRef, "data-testid": "carousel-layout", style: fadeViewportStyle }, data.map((item, index) => {
            const isActive = index === activeIndex;
            return (React.createElement("div", { key: item.id || index, "aria-hidden": !isActive, "data-active": isActive, "data-testid": "carousel-item", style: {
                    display: "block",
                    gridArea: "1 / 1",
                    inset: isActive ? undefined : 0,
                    opacity: isActive ? 1 : 0,
                    pointerEvents: isActive ? "auto" : "none",
                    position: isActive ? "relative" : "absolute",
                    transition: `opacity ${FADE_DURATION}ms ease-out`,
                    width: carouselWidth ?? "100%",
                } }, children({ index, item })));
        }))) : (React.createElement("div", { ref: layoutRef, "data-testid": "carousel-layout", style: slideViewportStyle, onClickCapture: handleClickCapture, onPointerCancel: handlePointerCancel, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: handlePointerUp },
            React.createElement("div", { "data-testid": "carousel-track", style: { ...trackStyle, visibility: carouselWidth ? "visible" : "hidden" } }, data.map((item, index) => (React.createElement("div", { key: item.id || index, "data-testid": "carousel-item", style: { ...style.carouselItem, flexShrink: 0, width: carouselWidth ?? "100%" } }, children({ index, item }))))))),
        bullets && data.length > 1 && (React.createElement("div", { "data-testid": "carousel-bullets", style: {
                ...style.bullets,
                ...styleWeb.bullets,
                display: "flex",
                justifyContent: "center",
                ...(customStyle?.bullets ? StyleSheet.flatten(customStyle.bullets) : undefined),
            } }, bullets({ activeIndex, count: data.length, onChange: handleOnActiveIndexChanged })))));
};
export { Carousel };
